import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// @mui material components
import Card from '@mui/material/Card'
import Badge from '@mui/material/Badge'
import Modal from '@mui/material/Modal'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import DataTable from 'examples/Tables/DataTable'
import MDAlert from 'components/MDAlert'
import { Tooltip, IconButton } from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import CrudService from 'services/cruds-service'
import { AbilityContext } from 'Can'
import { useAbility } from '@casl/react'
import { useNavigate } from 'react-router-dom'
import MDButton from 'components/MDButton'
import MDModal from 'components/MDModel'

function CampaignTemplateList() {
    let { state } = useLocation()
    const ability = useAbility(AbilityContext)
    const [data, setData] = useState([])
    const [tableData, setTableData] = useState([])
    const [notification, setNotification] = useState({
        value: false,
        text: '',
    })
    const [loader, setLoader] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [templateData, setTemplateData] = useState('')
    const navigate = useNavigate();

    useEffect(() => {
        ; (async () => {
            try {
                setLoader(true)
                const response = await CrudService.getAllCampaignTemplate()
                setData(response.data)
                setLoader(false)
            } catch (err) {
                console.error('err', err)
                setLoader(false)
            }
        })()
        document.title = `Rivio | Campaign | Template`
    }, [])

    useEffect(() => {
        if (!state) return
        setNotification({
            value: state.value,
            text: state.text,
        })
    }, [state])

    useEffect(() => {
        setTableData(getRows(data))
    }, [data])

    useEffect(() => {
        if (notification.value === true) {
            let timer = setTimeout(() => {
                setNotification({
                    value: false,
                    text: '',
                })
            }, 5000)
        }
    }, [notification])

    const clickAddHandler = () => {
        navigate('/campaign/template/new')
    }

    const clickEditHandler = async (id) => {
        navigate(`/campaign/template/edit/${id}`)
    }

    const clickDeleteHandler = async (e, id) => {
        try {
            if (!confirm('Are you sure you want to delete this Template?')) {
                e.nativeEvent.stopImmediatePropagation()
            } else {
                setLoader(true)
                await CrudService.removeCampaignTemplate(id)
                const response = await CrudService.getAllCampaignTemplate()
                setData(response.data)
                setNotification({
                    value: true,
                    text: 'The Template has been successfully deleted',
                })
                setLoader(false)
            }
        } catch (err) {
            console.error(err)
            if (err.hasOwnProperty('msg')) {
                setNotification({
                    value: true,
                    text: err.msg,
                })
            }
            setLoader(false)
            return null
        }
    }

    const showModalData = (value) => {
        setTemplateData(value)
        setShowModal(true)
    }

    const getRows = (info) => {
        let updatedInfo = info.map((row) => {
            return {
                id: row.template_id,
                name: row.template_name,
                description: row.template_content,
                created_at: row.created_at,
                updated_at: row.updated_at
            }
        })
        return updatedInfo
    }

    const dataTableData = {
        columns: [
            { Header: 'ID', accessor: 'id' },
            { Header: 'Name', accessor: 'name' },
            {
                Header: 'Content',
                accessor: 'description',
                Cell: (info) => {
                    return (
                        <>
                            <MDButton
                                variant="text"
                                color="info"
                                onClick={() => showModalData(info.value)}
                            >
                                View Content
                            </MDButton>
                        </>
                    )
                }
            },
            { Header: 'Created', accessor: 'created_at' },
            { Header: 'Updated', accessor: 'updated_at' },
            {
                Header: 'actions',
                disableSortBy: true,
                accessor: '',
                Cell: (info) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            {ability.can('edit', 'campaign') && (
                                <Tooltip title="Edit Template">
                                    <IconButton
                                        onClick={() =>
                                            clickEditHandler(info.cell.row.original.id)
                                        }
                                    >
                                        <MDTypography>
                                            <EditIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}

                            {ability.can('delete', 'campaign') && (
                                <Tooltip title="Delete Template">
                                    <IconButton
                                        onClick={(e) =>
                                            clickDeleteHandler(e, info.cell.row.original.id)
                                        }
                                    >
                                        <MDTypography>
                                            <DeleteIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    )
                },
            },
        ],

        rows: tableData,
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {notification.value && (
                <MDAlert color="info" my="20px">
                    <MDTypography variant="body2" color="white">
                        {notification.text || ''}
                    </MDTypography>
                </MDAlert>
            )}
            <MDBox pt={1}>
                <MDBox >
                    <Card>
                        <MDBox
                            p={3}
                            lineHeight={1}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <MDTypography variant="h5" fontWeight="medium">
                                Campaign Template
                            </MDTypography>
                            {ability.can('create', 'campaign') && (
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    size="small"
                                    type="submit"
                                    onClick={clickAddHandler}
                                >
                                    + Add Template
                                </MDButton>
                            )}
                        </MDBox>

                        <DataTable table={dataTableData} canSearch={true} tableHeight="calc(100vh - 220px)" loader={loader} />
                    </Card>
                </MDBox>
            </MDBox>
            <MDModal
                open={showModal}
                onClose={() => setShowModal(false)}
                title="Template Content Preview"
                content={
                    <iframe
                        srcDoc={templateData}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title="Template Preview"
                    />
                }
            />
            <Footer />
        </DashboardLayout>
    )
}

export default CampaignTemplateList